import { Suspense, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  LinearProgress,
  Link,
  Toolbar,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import { useTheme } from '@mui/material/styles';
import { useIsFetching } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDisplayLocale } from '../units/useDisplayLocale';
import { useAuth } from '../auth/AuthProvider';
import { SideNav, DRAWER_WIDTH, RAIL_WIDTH } from './SideNav';
import { useLocalStorageState } from '../hooks/useLocalStorageState';
import { TenantSelector } from './TenantSelector';
import { NotificationCentre } from './NotificationCentre';
import { UserMenu } from './UserMenu';
import { Breadcrumbs } from './Breadcrumbs';
import { ErrorBoundary } from './ErrorBoundary';
import { LoadingSkeleton } from './common/LoadingSkeleton';
import { ChromeProvider, useChrome } from './chrome/ChromeProvider';

function CommandTrigger() {
  const { openPalette } = useChrome();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  if (!desktop) {
    return (
      <IconButton onClick={openPalette} aria-label="Open command palette">
        <SearchIcon />
      </IconButton>
    );
  }
  return (
    <Button
      onClick={openPalette}
      variant="outlined"
      color="inherit"
      startIcon={<SearchIcon fontSize="small" />}
      aria-keyshortcuts="Control+K"
      sx={{
        justifyContent: 'flex-start',
        textTransform: 'none',
        color: 'text.secondary',
        borderColor: 'divider',
        width: 320,
        height: 36,
      }}
    >
      <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>
        Search or jump to…
      </Box>
      <Box
        component="kbd"
        sx={{
          fontFamily: 'var(--mono)',
          fontSize: '0.68rem',
          px: 0.6,
          py: 0.1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 0.75,
        }}
      >
        ⌘K
      </Box>
    </Button>
  );
}

/** True on platform-scoped routes, which have no active tenant. */
function usePlatformScope(): boolean {
  const { pathname } = useLocation();
  return pathname === '/platform' || pathname.startsWith('/platform/');
}

function ShellInner() {
  // Points the shared date formatters at this tenant's resolved locale, before any child
  // renders a date, and keys the routed area so a saved change repaints what is already on
  // screen. See `useDisplayLocale`.
  const displayLocale = useDisplayLocale();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const platformScope = usePlatformScope();
  const { assumedTenant, exitAssumedTenant, assumedPartner, exitAssumedPartner } = useAuth();
  const navigate = useNavigate();

  /**
   * Leaving puts the reader back where they ENTERED from.
   *
   * ⚠️ Reported as *"when I click on leave tenant, shouldn't it go to the page where I had
   * clicked to enter the tenant"* — it went to the platform home instead, because leaving
   * does not navigate and the tenant-less redirect in `routes.tsx` then fired from whatever
   * tenant-scoped page they were on. That redirect is right about the page no longer
   * working; what it cannot know is where they came FROM.
   *
   * ⚠️ The exit runs FIRST and the navigation second. Navigating while the context is still
   * held would render the destination once under the old context — a partner console drawn
   * for a tenant the reader is about to leave — and the query cache is cleared by the exit,
   * so the wasted render also refetches.
   */
  const leave = (exit: () => void, returnTo: string | undefined) => {
    exit();
    // Absent means the reader entered from a tenant-scoped page. Nothing to return to that
    // still works, so the redirect handles it exactly as before.
    if (returnTo) navigate(returnTo);
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  /*
   * ⚠️ **PERSISTED, because it is a working preference rather than a page state.** Somebody who
   * collapses the rail wants it collapsed tomorrow; resetting on every load would make the
   * control read as broken. Local, like the list-view preferences beside it — it is a fact
   * about this browser, not about the account, and a reader on a laptop and a wide monitor
   * legitimately wants different answers.
   *
   * ⚠️ **DEFAULTS EXPANDED.** The labels are what make thirteen icons legible, and a reader who
   * has never seen this product must not meet it as a column of glyphs. An unreadable stored
   * value falls back the same way, for the reason the question deck's does.
   */
  const [navCollapsed, setNavCollapsed] = useLocalStorageState('oip.nav.collapsed', false);
  /*
   * ⚠️ The PHONE drawer is never collapsed, whatever is stored: it is opened by tapping a menu
   * button, so a reader arrives there having explicitly asked to read the labels.
   */
  const navWidth = desktop && navCollapsed ? RAIL_WIDTH : DRAWER_WIDTH;
  const fetching = useIsFetching();
  const { openHelp } = useChrome();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Link
        href="#main-content"
        sx={{
          position: 'absolute',
          left: 8,
          top: -48,
          zIndex: (t) => t.zIndex.tooltip + 1,
          px: 2,
          py: 1,
          borderRadius: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          transition: 'top .15s',
          '&:focus': { top: 8 },
        }}
      >
        Skip to content
      </Link>

      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1, width: '100%' }}>
        {fetching > 0 && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }}
            aria-label="Loading"
          />
        )}
        <Toolbar variant="dense" sx={{ gap: 1, minHeight: 56 }}>
          {!desktop && (
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box
            /*
             * ⚠️ Keeps the command bar in the same place as the nav beneath it. Tied to
             * `navWidth` rather than the constant, or collapsing the rail leaves a 176px gap
             * where the menu used to be — measured on the built page, not reasoned about.
             */
            sx={{
              width: desktop ? navWidth - 16 : 0,
              display: { xs: 'none', md: 'block' },
              transition: theme.transitions.create('width', {
                duration: theme.transitions.duration.shorter,
              }),
            }}
          />
          <CommandTrigger />
          <Box sx={{ flex: 1 }} />
          {/*
            On platform routes there is no active tenant, so showing the tenant
            selector would imply a scope that is not in effect. The stored
            selection is untouched — it is restored on return to tenant routes.
          */}
          {assumedTenant && !platformScope ? (
            <Chip
              icon={<ShieldOutlinedIcon />}
              label={`Viewing ${assumedTenant.name}`}
              size="small"
              color="warning"
              variant="outlined"
              data-testid="assumed-tenant-indicator"
            />
          ) : platformScope ? (
            <Chip
              icon={<ShieldOutlinedIcon />}
              label="Platform administration"
              size="small"
              color="primary"
              variant="outlined"
              data-testid="platform-scope-indicator"
            />
          ) : (
            <TenantSelector />
          )}
          <Tooltip title="Keyboard shortcuts (?)">
            <IconButton onClick={openHelp} aria-label="Keyboard shortcuts">
              <KeyboardOutlinedIcon />
            </IconButton>
          </Tooltip>
          <NotificationCentre />
          <UserMenu />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        aria-label="Primary"
        sx={{
          width: { md: navWidth },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        {desktop ? (
          <Drawer
            variant="permanent"
            open
            sx={{
              '& .MuiDrawer-paper': {
                width: navWidth,
                borderRight: `1px solid ${theme.palette.divider}`,
                top: 56,
                height: 'calc(100% - 56px)',
                overflowX: 'hidden',
                /*
                 * ⚠️ The paper and the RESERVED SPACE must animate together, or the page
                 * content jumps to its new position while the drawer is still sliding and the
                 * two cross over. Same duration, same easing, both driven by `navWidth`.
                 */
                transition: theme.transitions.create('width', {
                  duration: theme.transitions.duration.shorter,
                }),
              },
            }}
          >
            <SideNav collapsed={navCollapsed} onToggleCollapse={() => setNavCollapsed(!navCollapsed)} />
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
          >
            <SideNav onNavigate={() => setMobileOpen(false)} />
          </Drawer>
        )}
      </Box>

      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', outline: 'none' }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 56 }} />
        <Box
          sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          {assumedPartner && (
            /*
              ⚠️ A SEPARATE banner from the tenant one, and BOTH can be on screen: entering
              a partner and then opening one of its companies are independent acts, and a
              single banner would make leaving one silently leave the other.

              It says what is being LOOKED AT and who is looking — nobody has been
              impersonated, and every request is recorded against this reader. Without that
              sentence the console is indistinguishable from the partner's own, which is
              the state `assumedByPlatformAdmin` exists to prevent one tier down.
            */
            <Alert
              severity="warning"
              variant="outlined"
              sx={{ mb: 2 }}
              action={
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<LogoutOutlinedIcon />}
                  onClick={() => leave(exitAssumedPartner, assumedPartner.returnTo)}
                >
                  Leave partner
                </Button>
              }
            >
              You are viewing{' '}
              <strong>
                {assumedPartner.name}
                {assumedPartner.resellerName !== null && ` · ${assumedPartner.resellerName}`}
              </strong>{' '}
              as a platform administrator, not as one of their people. Every request is
              recorded against you.
            </Alert>
          )}
          {assumedTenant && (
            /*
                A platform administrator is inside a tenant they do not belong
                to. Stating it persistently — and offering the way out — is what
                stops an operator forgetting whose data they are looking at.
              */
            <Alert
              severity="warning"
              variant="outlined"
              sx={{ mb: 2 }}
              action={
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<LogoutOutlinedIcon />}
                  onClick={() => leave(exitAssumedTenant, assumedTenant.returnTo)}
                >
                  Leave tenant
                </Button>
              }
            >
              You are viewing <strong>{assumedTenant.name}</strong> as a platform administrator.
              Every request is recorded in this tenant&rsquo;s audit trail.
            </Alert>
          )}
          <Breadcrumbs />
        </Box>
        {/*
          ⚠️ KEYED on the locale, so saving the preference repaints every date already on
          screen. The formatters are plain functions rather than hooks, so a component that
          calls `absolute()` and nothing else has no subscription and would otherwise keep
          rendering the old convention until something unrelated re-rendered it — one page
          in dd/mm and the next in mm/dd, which nobody would report because each looks
          plausible alone. A remount is heavy for an ordinary state change and right for
          this one: it happens when somebody saves a setting, and never otherwise.
        */}
        <Box key={displayLocale} sx={{ p: { xs: 2, md: 3 }, flex: 1, minWidth: 0 }}>
          <ErrorBoundary>
            <Suspense fallback={<LoadingSkeleton rows={8} />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}

export function AppShell() {
  return (
    <ChromeProvider>
      <ShellInner />
    </ChromeProvider>
  );
}
